import type { WorkspaceFile } from "../types";

export const PREVIEW_CHANNEL = "codex-browser-preview-v2";

export interface PreviewFilePayload {
  path: string;
  kind: "text" | "binary";
  content: string | ArrayBuffer;
  mimeType: string;
}

export interface PreviewRenderMessage {
  channel: typeof PREVIEW_CHANNEL;
  type: "render";
  entryPath: string;
  entryHash: string;
  files: PreviewFilePayload[];
}

export type PreviewHostMessage =
  | { channel: typeof PREVIEW_CHANNEL; type: "ready" }
  | { channel: typeof PREVIEW_CHANNEL; type: "rendered"; path: string }
  | { channel: typeof PREVIEW_CHANNEL; type: "navigate"; path: string; hash?: string }
  | { channel: typeof PREVIEW_CHANNEL; type: "error"; message: string };

let legacyCleanupPromise: Promise<void> | null = null;

export function buildPreviewHostUrl(version: string): string {
  return `/preview-host?v=${encodeURIComponent(version)}`;
}

export function previewFiles(files: WorkspaceFile[]): PreviewFilePayload[] {
  return files.map(({ path, kind, content, mimeType }) => ({ path, kind, content, mimeType }));
}

export function isPreviewHostMessage(value: unknown): value is PreviewHostMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<PreviewHostMessage>;
  if (message.channel !== PREVIEW_CHANNEL) return false;
  if (message.type === "ready") return true;
  if (message.type === "rendered") return typeof message.path === "string";
  if (message.type === "navigate") return typeof message.path === "string" && (message.hash === undefined || typeof message.hash === "string");
  if (message.type === "error") return typeof message.message === "string";
  return false;
}

export async function removeLegacyPreviewWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  if (!legacyCleanupPromise) {
    legacyCleanupPromise = navigator.serviceWorker.getRegistrations()
      .then(async (registrations) => {
        await Promise.all(registrations.map(async (registration) => {
          const worker = registration.active ?? registration.waiting ?? registration.installing;
          if (!worker) return;
          const script = new URL(worker.scriptURL);
          if (script.origin === window.location.origin && script.pathname === "/preview-sw.js") {
            await registration.unregister();
          }
        }));
        if ("caches" in window) await window.caches.delete("codex-browser-preview-context-v1");
      })
      .catch(() => undefined);
  }
  await legacyCleanupPromise;
}
